import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { H2, H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph3 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import LazyCallBack from '~/src/components/LazyCallBack';
import { EPrivateHouse } from '~/src/features/internet/internet-represent';
import getSpeedLineItems, { TGetSpeedLineItemsReturnType } from '~/src/features/request/helpers/getSpeedLineItems';
import { TState } from '~/src/store';
import { selectAllTariffs } from '~/src/store/selectors/filteredBundles';

import SpeedLineSelector from './SpeedLineSelector';

import * as ST from './styled';
import * as STA from './styled-animation';

interface IProps {
    cityIn: string,
    privateHouse?: EPrivateHouse,
}

export const RepresentTop = (
    {
        cityIn,
        privateHouse,
    }: IProps,
): JSX.Element => {
    const { allTariffs, isAuth } = useSelector((state: TState) => ({
        allTariffs: selectAllTariffs(state),
        isAuth: state.auth.isAuth,
    }));
    const speedLineItems = useMemo<TGetSpeedLineItemsReturnType>(() => {
        return getSpeedLineItems(allTariffs, !!privateHouse);
    }, [privateHouse, allTariffs]);
    const isVillage = privateHouse === EPrivateHouse.Village;
    const isCountryHouse = privateHouse === EPrivateHouse.CountryHouse;

    return (
        <ST.RepresentTop>
            <ST.StyledContainer>
                <ST.Content>
                    <ST.BlockWithSpeedLineWrapper>
                        <TextWrapper>
                            <H4
                                $smaller
                                as={'h1'}
                                $color={'TextSecondary'}
                            >
                                {isVillage
                                    ? 'Интернет в частный дом'
                                    : isCountryHouse
                                        ? 'Интернет на дачу'
                                        : `Домашний интернет в ${cityIn || 'городе'}`}
                            </H4>
                            <H2 $smaller as={'span'}>
                                {isCountryHouse ? 'Оставайся на связи вдали от города' : 'Интернет, каким он должен быть'}
                            </H2>
                            <Paragraph3>
                                {isCountryHouse
                                    ? <>Стабильное подключение для удаленной работы и&nbsp;непрерывного общения</>
                                    : 'Высокая скорость, стабильное подключение, круглосуточная поддержка и выгодные цены'
                                }
                            </Paragraph3>
                        </TextWrapper>

                        {speedLineItems
                            ? (
                                <SpeedLineSelector
                                    speedLineItems={speedLineItems}
                                    isAuth={isAuth}
                                    isCottage={isVillage || isCountryHouse}
                                />
                            )
                            : (
                                <LazyCallBack
                                    tariffName={'internet'}
                                    requestDataParams={{ product_id: [EProductsId.internet] }}
                                    withPopupResult={true}
                                    title={'Оставьте заявку в один клик'}
                                    description={<></>}
                                />
                            )
                        }
                    </ST.BlockWithSpeedLineWrapper>

                    <STA.ImgContent>
                        <STA.AnimationWrap>
                            <STA.CenterImg
                                src={require('~/static/images/internet/represent/top-block-animation/center-img.png')}
                            />
                            <STA.LeftImg
                                src={require('~/static/images/internet/represent/top-block-animation/left-img.png')}
                            />
                            <STA.RightImg
                                src={require('~/static/images/internet/represent/top-block-animation/right-img.png')}
                            />
                            <STA.BottomImg
                                src={require('~/static/images/internet/represent/top-block-animation/bottom-img.png')}
                            />
                        </STA.AnimationWrap>
                    </STA.ImgContent>
                </ST.Content>
            </ST.StyledContainer>
        </ST.RepresentTop>
    );
};
